import { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { RequireAuth } from "./features/auth/components/require-auth";
import { RequireAuthNoCompany } from "./features/auth/components/require-auth-no-company";
import { RequireAuthNoSubscription } from "./features/auth/components/require-auth-no-subscription";
import { PublicRoute } from "./features/auth/components/public-route";
import { RequirePermission } from "./features/auth/components/require-permission";
import { AuthErrorBoundary } from "./features/auth/components/auth-error-boundary";
import { TokenRefreshProvider } from "./features/auth/components/token-refresh-provider";
import { AuthProvider } from "./features/auth/context/auth-context";
import { PermissionHintProvider } from "./features/auth/context/permission-hint-context";
import { PermissionHintFab } from "./components/common/permission-hint-fab";
import { UnsupportedBrowserBanner } from "./components/common/unsupported-browser-banner";
import { AppLayout } from "./components/layouts/app-layout";
import {
  INDUSTRIAL_FORMULA_CREATE_PERMISSION,
  INDUSTRIAL_FORMULA_READ_PERMISSION,
  INDUSTRIAL_FORMULA_UPDATE_PERMISSION,
} from "./features/industrial-formula/lib/industrial-formula-permissions";
import { PERMISSIONS } from "./constants/permissions";

// Componentes críticos (sin lazy loading)
import { LoginPage } from "./features/auth/pages/login-page";
import { SelectCompanyPage } from "./features/auth/pages/select-company-page";
import { SelectSubscriptionPage } from "./features/auth/pages/select-subscription-page";
import { ForgotPasswordPage } from "./features/auth/pages/forgot-password-page";
import { ResetPasswordPage } from "./features/auth/pages/reset-password-page";
import { SetInitialPasswordPage } from "./features/auth/pages/set-initial-password-page";

// Lazy loading para todas las páginas protegidas
const DashboardPage = lazy(() =>
  import("./features/dashboard/pages/dashboard-page").then((m) => ({
    default: m.DashboardPage,
  })),
);
const UsersPage = lazy(() =>
  import("./features/users/pages/users-page").then((m) => ({
    default: m.UsersPage,
  })),
);
const UserFormPage = lazy(() =>
  import("./features/users/pages/user-form-page").then((m) => ({
    default: m.UserFormPage,
  })),
);
const OrganizationPositionsPage = lazy(() =>
  import("./features/users/pages/organization-positions-page").then((m) => ({
    default: m.OrganizationPositionsPage,
  })),
);
const ProfilePage = lazy(() =>
  import("./features/auth/pages/profile-page").then((m) => ({
    default: m.ProfilePage,
  })),
);
const RolesPage = lazy(() => import("./features/roles/pages/roles-page"));
const RoleFormPage = lazy(
  () => import("./features/roles/pages/role-form-page"),
);
const PermissionsPage = lazy(() =>
  import("./features/permissions/pages/permissions-page").then((m) => ({
    default: m.PermissionsPage,
  })),
);

// Master Data - Lazy loading
const MaterialListPage = lazy(() =>
  import("./features/material/pages/material-list-page").then((m) => ({
    default: m.MaterialListPage,
  })),
);
const MaterialDetailPage = lazy(() =>
  import("./features/material/pages/material-detail-page").then((m) => ({
    default: m.MaterialDetailPage,
  })),
);
const MaterialForm = lazy(() =>
  import("./features/material/components/material-form").then((m) => ({
    default: m.MaterialForm,
  })),
);
const MaterialImportPage = lazy(() =>
  import("./features/data-import/pages/material-import-page").then((m) => ({
    default: m.MaterialImportPage,
  })),
);
const MaterialUnitConversionListPage = lazy(() =>
  import("./features/material-unit-conversion/pages/material-unit-conversion-list-page").then(
    (m) => ({ default: m.MaterialUnitConversionListPage }),
  ),
);
const MaterialUnitConversionForm = lazy(() =>
  import("./features/material-unit-conversion/components/material-unit-conversion-form").then(
    (m) => ({ default: m.MaterialUnitConversionForm }),
  ),
);
const PlantListPage = lazy(() =>
  import("./features/plant/pages/plant-list-page").then((m) => ({
    default: m.PlantListPage,
  })),
);
const PlantForm = lazy(() =>
  import("./features/plant/components/plant-form").then((m) => ({
    default: m.PlantForm,
  })),
);
const StorageLocListPage = lazy(() =>
  import("./features/storage-loc/pages/storage-loc-list-page").then((m) => ({
    default: m.StorageLocListPage,
  })),
);
const StorageLocForm = lazy(() =>
  import("./features/storage-loc/components/storage-loc-form").then((m) => ({
    default: m.StorageLocForm,
  })),
);
const StorageBinListPage = lazy(() =>
  import("./features/storage-bin/pages/storage-bin-list-page").then((m) => ({
    default: m.StorageBinListPage,
  })),
);
const StorageLocTemplateListPage = lazy(() =>
  import("./features/storage-loc-layout-template/pages/storage-loc-template-list-page").then(
    (m) => ({ default: m.StorageLocTemplateListPage }),
  ),
);
const StorageLocTemplateForm = lazy(() =>
  import("./features/storage-loc-layout-template/components/storage-loc-template-form").then(
    (m) => ({ default: m.StorageLocTemplateForm }),
  ),
);
const BatchListPage = lazy(() =>
  import("./features/batch/pages/batch-list-page").then((m) => ({
    default: m.BatchListPage,
  })),
);
const BatchForm = lazy(() =>
  import("./features/batch/components/batch-form").then((m) => ({
    default: m.BatchForm,
  })),
);
const BatchImportPage = lazy(() =>
  import("./features/data-import/pages/batch-import-page").then((m) => ({
    default: m.BatchImportPage,
  })),
);
const BatchTemplateListPage = lazy(() =>
  import("./features/batch-template/pages/batch-template-list-page").then(
    (m) => ({
      default: m.BatchTemplateListPage,
    }),
  ),
);
const BatchTemplateFormPage = lazy(() =>
  import("./features/batch-template/pages/batch-template-form-page").then(
    (m) => ({
      default: m.BatchTemplateFormPage,
    }),
  ),
);
const UnitsListPage = lazy(() =>
  import("./features/units/pages/units-list-page").then((m) => ({
    default: m.UnitsListPage,
  })),
);
const UnitsForm = lazy(() =>
  import("./features/units/components/units-form").then((m) => ({
    default: m.UnitsForm,
  })),
);
const UnitImportPage = lazy(() =>
  import("./features/data-import/pages/unit-import-page").then((m) => ({
    default: m.UnitImportPage,
  })),
);
const PlantTypeListPage = lazy(() =>
  import("./features/plant-types/pages/plant-type-list-page").then((m) => ({
    default: m.PlantTypeListPage,
  })),
);
const PlantTypeForm = lazy(() =>
  import("./features/plant-types/components/plant-type-form").then((m) => ({
    default: m.PlantTypeForm,
  })),
);
const DispatchGuideStatusListPage = lazy(() =>
  import("./features/dispatch-guide-status/pages/dispatch-guide-status-list-page").then(
    (m) => ({
      default: m.DispatchGuideStatusListPage,
    }),
  ),
);
const DispatchGuideStatusForm = lazy(() =>
  import("./features/dispatch-guide-status/components/dispatch-guide-status-form").then(
    (m) => ({
      default: m.DispatchGuideStatusForm,
    }),
  ),
);
const DispatchGuideStatusTransitionListPage = lazy(() =>
  import("./features/dispatch-guide-status/pages/dispatch-guide-status-transition-list-page").then(
    (m) => ({
      default: m.DispatchGuideStatusTransitionListPage,
    }),
  ),
);
const DispatchGuideStatusTransitionForm = lazy(() =>
  import("./features/dispatch-guide-status/components/dispatch-guide-status-transition-form").then(
    (m) => ({
      default: m.DispatchGuideStatusTransitionForm,
    }),
  ),
);
const DispatchFlowProfileListPage = lazy(() =>
  import("./features/dispatch-guide-status/pages/dispatch-flow-profile-list-page").then(
    (m) => ({
      default: m.DispatchFlowProfileListPage,
    }),
  ),
);
const DispatchFlowProfileForm = lazy(() =>
  import("./features/dispatch-guide-status/components/dispatch-flow-profile-form").then(
    (m) => ({
      default: m.DispatchFlowProfileForm,
    }),
  ),
);
const ApprovalFlowListPage = lazy(() =>
  import("./features/approval-flow/pages/approval-flow-list-page").then(
    (m) => ({
      default: m.ApprovalFlowListPage,
    }),
  ),
);
const ApprovalFlowFormPage = lazy(() =>
  import("./features/approval-flow/pages/approval-flow-form-page").then(
    (m) => ({
      default: m.ApprovalFlowFormPage,
    }),
  ),
);
const MaterialGroupListPage = lazy(() =>
  import("./features/material-groups/pages/material-group-list-page").then(
    (m) => ({
      default: m.MaterialGroupListPage,
    }),
  ),
);
const MaterialGroupForm = lazy(() =>
  import("./features/material-groups/components/material-group-form").then(
    (m) => ({
      default: m.MaterialGroupForm,
    }),
  ),
);
const MaterialGroupImportPage = lazy(() =>
  import("./features/data-import/pages/material-group-import-page").then(
    (m) => ({
      default: m.MaterialGroupImportPage,
    }),
  ),
);
const CompanyListPage = lazy(() =>
  import("./features/company/pages/company-list-page").then((m) => ({
    default: m.CompanyListPage,
  })),
);
const CompanyForm = lazy(() =>
  import("./features/company/components/company-form").then((m) => ({
    default: m.CompanyForm,
  })),
);
const CostCenterListPage = lazy(() =>
  import("./features/cost-center/pages/cost-center-list-page").then((m) => ({
    default: m.CostCenterListPage,
  })),
);
const CostCenterForm = lazy(() =>
  import("./features/cost-center/components/cost-center-form").then((m) => ({
    default: m.CostCenterForm,
  })),
);
const CostCenterImportPage = lazy(() =>
  import("./features/data-import/pages/cost-center-import-page").then((m) => ({
    default: m.CostCenterImportPage,
  })),
);
const ProductionOrderListPage = lazy(() =>
  import("./features/production-order/pages/production-order-list-page").then(
    (m) => ({
      default: m.ProductionOrderListPage,
    }),
  ),
);
const ProductionOrderForm = lazy(() =>
  import("./features/production-order/components/production-order-form").then(
    (m) => ({
      default: m.ProductionOrderForm,
    }),
  ),
);
const MovementTypeListPage = lazy(() =>
  import("./features/movement-type/pages/movement-type-list-page").then(
    (m) => ({
      default: m.MovementTypeListPage,
    }),
  ),
);
const MovementTypeForm = lazy(() =>
  import("./features/movement-type/components/movement-type-form").then(
    (m) => ({
      default: m.MovementTypeForm,
    }),
  ),
);
const MovementTypeImportPage = lazy(() =>
  import("./features/data-import/pages/movement-type-import-page").then(
    (m) => ({
      default: m.MovementTypeImportPage,
    }),
  ),
);
const VendorListPage = lazy(() =>
  import("./features/vendor/pages/vendor-list-page").then((m) => ({
    default: m.VendorListPage,
  })),
);
const VendorForm = lazy(() =>
  import("./features/vendor/components/vendor-form").then((m) => ({
    default: m.VendorForm,
  })),
);
const CustomerListPage = lazy(() =>
  import("./features/customer/pages/customer-list-page").then((m) => ({
    default: m.CustomerListPage,
  })),
);
const CustomerForm = lazy(() =>
  import("./features/customer/components/customer-form").then((m) => ({
    default: m.CustomerForm,
  })),
);
const PurchaseOrderListPage = lazy(() =>
  import("./features/purchase-order/pages/purchase-order-list-page").then(
    (m) => ({
      default: m.PurchaseOrderListPage,
    }),
  ),
);
const PurchaseOrderForm = lazy(() =>
  import("./features/purchase-order/components/purchase-order-form").then(
    (m) => ({
      default: m.PurchaseOrderForm,
    }),
  ),
);
const SalesOrderListPage = lazy(() =>
  import("./features/sales-order/pages/sales-order-list-page").then((m) => ({
    default: m.SalesOrderListPage,
  })),
);
const SalesOrderForm = lazy(() =>
  import("./features/sales-order/components/sales-order-form").then((m) => ({
    default: m.SalesOrderForm,
  })),
);
const PaymentTermListPage = lazy(() =>
  import("./features/payment-term/pages/payment-term-list-page").then((m) => ({
    default: m.PaymentTermListPage,
  })),
);
const PaymentTermForm = lazy(() =>
  import("./features/payment-term/components/payment-term-form").then((m) => ({
    default: m.PaymentTermForm,
  })),
);
const CommercialBusinessLineListPage = lazy(() =>
  import("./features/commercial-business-line/pages/commercial-business-line-list-page").then(
    (m) => ({
      default: m.CommercialBusinessLineListPage,
    }),
  ),
);
const CommercialBusinessLineForm = lazy(() =>
  import("./features/commercial-business-line/components/commercial-business-line-form").then(
    (m) => ({
      default: m.CommercialBusinessLineForm,
    }),
  ),
);
const VendorServiceListPage = lazy(() =>
  import("./features/vendor-service/pages/vendor-service-list-page").then(
    (m) => ({
      default: m.VendorServiceListPage,
    }),
  ),
);
const VendorServiceForm = lazy(() =>
  import("./features/vendor-service/components/vendor-service-form").then(
    (m) => ({
      default: m.VendorServiceForm,
    }),
  ),
);
const CommercialDocumentClassListPage = lazy(() =>
  import("./features/commercial-document-class/pages/commercial-document-class-list-page").then(
    (m) => ({
      default: m.CommercialDocumentClassListPage,
    }),
  ),
);
const CommercialDocumentClassForm = lazy(() =>
  import("./features/commercial-document-class/components/commercial-document-class-form").then(
    (m) => ({
      default: m.CommercialDocumentClassForm,
    }),
  ),
);

// Material Movement - Lazy loading
const MovementFormPage = lazy(() =>
  import("./features/material-movement/pages/movement-form-page").then((m) => ({
    default: m.MovementFormPage,
  })),
);
const MaterialMovementListPage = lazy(() =>
  import("./features/material-movement/pages/material-movement-list-page").then(
    (m) => ({
      default: m.MaterialMovementListPage,
    }),
  ),
);
const WorkOrderListPage = lazy(() =>
  import("./features/work-order/pages/work-order-list-page").then((m) => ({
    default: m.WorkOrderListPage,
  })),
);
const WorkOrderFormPage = lazy(() =>
  import("./features/work-order/pages/work-order-form-page").then((m) => ({
    default: m.WorkOrderFormPage,
  })),
);
const WorkOrderDetailPage = lazy(() =>
  import("./features/work-order/pages/work-order-detail-page").then((m) => ({
    default: m.WorkOrderDetailPage,
  })),
);
const WorkOrderTemplateListPage = lazy(() =>
  import("./features/work-order-template/pages/template-list-page").then(
    (m) => ({
      default: m.WorkOrderTemplateListPage,
    }),
  ),
);
const WorkOrderTemplateDetailPage = lazy(() =>
  import("./features/work-order-template/pages/template-detail-page").then(
    (m) => ({
      default: m.WorkOrderTemplateDetailPage,
    }),
  ),
);
const WorkOrderTemplateFormPage = lazy(() =>
  import("./features/work-order-template/pages/template-form-page").then(
    (m) => ({
      default: m.WorkOrderTemplateFormPage,
    }),
  ),
);
const WorkOrderActivityListPage = lazy(() =>
  import("./features/work-order-activity/pages/activity-list-page").then(
    (m) => ({
      default: m.WorkOrderActivityListPage,
    }),
  ),
);
const WorkOrderActivityFormPage = lazy(() =>
  import("./features/work-order-activity/pages/activity-form-page").then(
    (m) => ({
      default: m.WorkOrderActivityFormPage,
    }),
  ),
);

const IndustrialFormulaListPage = lazy(() =>
  import("./features/industrial-formula/pages/industrial-formula-list-page").then(
    (m) => ({
      default: m.IndustrialFormulaListPage,
    }),
  ),
);
const IndustrialFormulaEditorPage = lazy(() =>
  import("./features/industrial-formula/pages/industrial-formula-editor-page").then(
    (m) => ({
      default: m.IndustrialFormulaEditorPage,
    }),
  ),
);
const IndustrialFormulaProjectionsPage = lazy(() =>
  import("./features/industrial-formula/pages/industrial-formula-projections-page").then(
    (m) => ({
      default: m.IndustrialFormulaProjectionsPage,
    }),
  ),
);

// Dispatch Guide - Lazy loading
const DispatchGuideListPage = lazy(() =>
  import("./features/dispatch-guide/pages/dispatch-guide-list-page").then(
    (m) => ({
      default: m.DispatchGuideListPage,
    }),
  ),
);
const DispatchGuideQRPage = lazy(() =>
  import("./features/dispatch-guide/pages/dispatch-guide-qr-page").then(
    (m) => ({
      default: m.DispatchGuideQRPage,
    }),
  ),
);
const DispatchGuideConfirmedPage = lazy(() =>
  import("./features/dispatch-guide/pages/dispatch-guide-confirmed-page").then(
    (m) => ({
      default: m.DispatchGuideConfirmedPage,
    }),
  ),
);
const StockQRPage = lazy(() =>
  import("./features/stock/pages/stock-qr-page").then((m) => ({
    default: m.StockQRPage,
  })),
);
const MaterialMovementQrPage = lazy(() =>
  import("./features/material-movement/pages/material-movement-qr-page").then(
    (m) => ({ default: m.MaterialMovementQrPage }),
  ),
);
const PurchaseOrderQrPage = lazy(() =>
  import("./features/purchase-order/pages/purchase-order-qr-page").then(
    (m) => ({ default: m.PurchaseOrderQrPage }),
  ),
);

// Stock - Lazy loading
const StockListPage = lazy(() =>
  import("./features/stock/pages/stock-list-page").then((m) => ({
    default: m.StockListPage,
  })),
);

// Carrier - Lazy loading
const CarrierTypeListPage = lazy(() =>
  import("./features/carrier-type/pages/carrier-type-list-page").then((m) => ({
    default: m.CarrierTypeListPage,
  })),
);
const CarrierTypeForm = lazy(() =>
  import("./features/carrier-type/components/carrier-type-form").then((m) => ({
    default: m.CarrierTypeForm,
  })),
);
const CarrierListPage = lazy(() =>
  import("./features/carrier/pages/carrier-list-page").then((m) => ({
    default: m.CarrierListPage,
  })),
);
const CarrierForm = lazy(() =>
  import("./features/carrier/components/carrier-form").then((m) => ({
    default: m.CarrierForm,
  })),
);
const DriverListPage = lazy(() =>
  import("./features/driver/pages/driver-list-page").then((m) => ({
    default: m.DriverListPage,
  })),
);
const DriverForm = lazy(() =>
  import("./features/driver/components/driver-form").then((m) => ({
    default: m.DriverForm,
  })),
);
const FieldListPage = lazy(() =>
  import("./features/field/pages/field-list-page").then((m) => ({
    default: m.FieldListPage,
  })),
);
const FieldForm = lazy(() =>
  import("./features/field/components/field-form").then((m) => ({
    default: m.FieldForm,
  })),
);
const EmployeeListPage = lazy(() =>
  import("./features/employee/pages/employee-list-page").then((m) => ({
    default: m.EmployeeListPage,
  })),
);
const EmployeeForm = lazy(() =>
  import("./features/employee/components/employee-form").then((m) => ({
    default: m.EmployeeForm,
  })),
);
const CuartelListPage = lazy(() =>
  import("./features/cuartel/pages/cuartel-list-page").then((m) => ({
    default: m.CuartelListPage,
  })),
);
const CuartelForm = lazy(() =>
  import("./features/cuartel/components/cuartel-form").then((m) => ({
    default: m.CuartelForm,
  })),
);
const AttendanceListPage = lazy(() =>
  import("./features/attendance/pages/attendance-list-page").then((m) => ({
    default: m.AttendanceListPage,
  })),
);
const AttendanceDailyPage = lazy(() =>
  import("./features/attendance/pages/attendance-daily-page").then((m) => ({
    default: m.AttendanceDailyPage,
  })),
);
const LaborActivityListPage = lazy(() =>
  import("./features/labor-activity/pages/labor-activity-list-page").then(
    (m) => ({ default: m.LaborActivityListPage }),
  ),
);
const LaborActivityForm = lazy(() =>
  import("./features/labor-activity/components/labor-activity-form").then(
    (m) => ({ default: m.LaborActivityForm }),
  ),
);
const LaborRateListPage = lazy(() =>
  import("./features/labor-rate/pages/labor-rate-list-page").then((m) => ({
    default: m.LaborRateListPage,
  })),
);
const LaborRateForm = lazy(() =>
  import("./features/labor-rate/components/labor-rate-form").then((m) => ({
    default: m.LaborRateForm,
  })),
);
const LaborRateGeneralListPage = lazy(() =>
  import("./features/labor-rate-general/pages/labor-rate-general-list-page").then(
    (m) => ({ default: m.LaborRateGeneralListPage }),
  ),
);
const LaborRateGeneralForm = lazy(() =>
  import("./features/labor-rate-general/components/labor-rate-general-form").then(
    (m) => ({ default: m.LaborRateGeneralForm }),
  ),
);
const SpeciesListPage = lazy(() =>
  import("./features/species/pages/species-list-page").then((m) => ({
    default: m.SpeciesListPage,
  })),
);
const SpeciesForm = lazy(() =>
  import("./features/species/components/species-form").then((m) => ({
    default: m.SpeciesForm,
  })),
);
const VarietyListPage = lazy(() =>
  import("./features/variety/pages/variety-list-page").then((m) => ({
    default: m.VarietyListPage,
  })),
);
const VarietyForm = lazy(() =>
  import("./features/variety/components/variety-form").then((m) => ({
    default: m.VarietyForm,
  })),
);
const LaborRetentionListPage = lazy(() =>
  import("./features/labor-retention/pages/labor-retention-list-page").then(
    (m) => ({ default: m.LaborRetentionListPage }),
  ),
);
const LaborRetentionForm = lazy(() =>
  import("./features/labor-retention/components/labor-retention-form").then(
    (m) => ({ default: m.LaborRetentionForm }),
  ),
);
const LaborTicketListPage = lazy(() =>
  import("./features/labor-ticket/pages/labor-ticket-list-page").then((m) => ({
    default: m.LaborTicketListPage,
  })),
);
const LaborTicketDailyPage = lazy(() =>
  import("./features/labor-ticket/pages/labor-ticket-daily-page").then((m) => ({
    default: m.LaborTicketDailyPage,
  })),
);
const VehicleListPage = lazy(() =>
  import("./features/vehicle/pages/vehicle-list-page").then((m) => ({
    default: m.VehicleListPage,
  })),
);
const VehicleForm = lazy(() =>
  import("./features/vehicle/components/vehicle-form").then((m) => ({
    default: m.VehicleForm,
  })),
);
const VehicleAssignmentListPage = lazy(() =>
  import("./features/vehicle-assignment/pages/vehicle-assignment-list-page").then(
    (m) => ({
      default: m.VehicleAssignmentListPage,
    }),
  ),
);
const VehicleAssignmentForm = lazy(() =>
  import("./features/vehicle-assignment/components/vehicle-assignment-form").then(
    (m) => ({
      default: m.VehicleAssignmentForm,
    }),
  ),
);

// Route - Lazy loading
const RouteListPage = lazy(() =>
  import("./features/route/pages/route-list-page").then((m) => ({
    default: m.RouteListPage,
  })),
);
const RouteForm = lazy(() =>
  import("./features/route/components/route-form").then((m) => ({
    default: m.RouteForm,
  })),
);

// Trip - Lazy loading
const TripListPage = lazy(() =>
  import("./features/trip/pages/trip-list-page").then((m) => ({
    default: m.TripListPage,
  })),
);
const TripForm = lazy(() =>
  import("./features/trip/components/trip-form").then((m) => ({
    default: m.TripForm,
  })),
);
const TripExecutePage = lazy(() =>
  import("./features/trip/pages/trip-execute-page").then((m) => ({
    default: m.TripExecutePage,
  })),
);
const TripTrackPage = lazy(() =>
  import("./features/trip/pages/trip-track-page").then((m) => ({
    default: m.TripTrackPage,
  })),
);

// Material Request - Lazy loading
const MaterialRequestListPage = lazy(() =>
  import("./features/material-request/pages/material-request-list-page").then(
    (m) => ({
      default: m.MaterialRequestListPage,
    }),
  ),
);
const MaterialRequestCreatePage = lazy(() =>
  import("./features/material-request/pages/material-request-create-page").then(
    (m) => ({
      default: m.MaterialRequestCreatePage,
    }),
  ),
);
const MaterialRequestDetailPage = lazy(() =>
  import("./features/material-request/pages/material-request-detail-page").then(
    (m) => ({
      default: m.MaterialRequestDetailPage,
    }),
  ),
);

// Platform - Lazy loading
const SubscriptionListPage = lazy(() =>
  import("./features/subscription/pages/subscription-list-page").then((m) => ({
    default: m.SubscriptionListPage,
  })),
);
const SubscriptionUsersPage = lazy(() =>
  import("./features/subscription/pages/subscription-users-page").then((m) => ({
    default: m.SubscriptionUsersPage,
  })),
);
const PlanListPage = lazy(() =>
  import("./features/plan/pages/plan-list-page").then((m) => ({
    default: m.PlanListPage,
  })),
);
const PlanFormPage = lazy(() =>
  import("./features/plan/components/plan-form-page").then((m) => ({
    default: m.PlanFormPage,
  })),
);
const PlanPriceListPage = lazy(() =>
  import("./features/plan-price/pages/plan-price-list-page").then((m) => ({
    default: m.PlanPriceListPage,
  })),
);
const PlanPriceFormPage = lazy(() =>
  import("./features/plan-price/pages/plan-price-form-page").then((m) => ({
    default: m.PlanPriceFormPage,
  })),
);
const InvoiceListPage = lazy(() =>
  import("./features/invoice/pages/invoice-list-page").then((m) => ({
    default: m.InvoiceListPage,
  })),
);
const AuditListPage = lazy(() =>
  import("./features/audit/pages/audit-list-page").then((m) => ({
    default: m.AuditListPage,
  })),
);
const CurrencyListPage = lazy(() =>
  import("./features/currency/pages/currency-list-page").then((m) => ({
    default: m.CurrencyListPage,
  })),
);
const CurrencyForm = lazy(() =>
  import("./features/currency/components/currency-form").then((m) => ({
    default: m.CurrencyForm,
  })),
);
const SubscriptionForm = lazy(() =>
  import("./features/subscription/components/subscription-form").then((m) => ({
    default: m.SubscriptionForm,
  })),
);
const UnitCoreListPage = lazy(() =>
  import("./features/unit-core/pages/unit-core-list-page").then((m) => ({
    default: m.UnitCoreListPage,
  })),
);
const UnitCoreFormPage = lazy(() =>
  import("./features/unit-core/pages/unit-core-form-page").then((m) => ({
    default: m.UnitCoreFormPage,
  })),
);

// Fallback de carga para Suspense
const PageLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Cargando...</p>
    </div>
  </div>
);

const WelcomePage = lazy(() =>
  import("./features/welcome/pages/welcome-page").then((m) => ({
    default: m.WelcomePage,
  })),
);

const NotFoundPage = lazy(() =>
  import("./features/errors/pages/not-found-page").then((m) => ({
    default: m.NotFoundPage,
  })),
);

function App() {
  return (
    <AuthProvider>
      <PermissionHintProvider>
        <TokenRefreshProvider>
          <AuthErrorBoundary>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <UnsupportedBrowserBanner />
              <div className="App flex min-h-svh flex-col pt-[var(--browser-compat-banner-height,0px)]">
                <Routes>
                  {/* Rutas públicas - sin layout */}
                  <Route
                    path="/login"
                    element={
                      <PublicRoute>
                        <LoginPage />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/forgot-password"
                    element={
                      <PublicRoute>
                        <ForgotPasswordPage />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/reset-password"
                    element={
                      <PublicRoute>
                        <ResetPasswordPage />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/set-initial-password"
                    element={
                      <PublicRoute>
                        <SetInitialPasswordPage />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/select-subscription"
                    element={
                      <RequireAuthNoSubscription>
                        <SelectSubscriptionPage />
                      </RequireAuthNoSubscription>
                    }
                  />
                  <Route
                    path="/select-company"
                    element={
                      <RequireAuthNoCompany>
                        <SelectCompanyPage />
                      </RequireAuthNoCompany>
                    }
                  />
                  {/* Rutas públicas para QR - sin layout */}
                  <Route
                    path="/dispatch-guides/qr/:id"
                    element={
                      <Suspense fallback={<PageLoadingFallback />}>
                        <DispatchGuideQRPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/dispatch-guides/qr/confirmed"
                    element={
                      <Suspense fallback={<PageLoadingFallback />}>
                        <DispatchGuideConfirmedPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/stock/qr/storage/:id"
                    element={
                      <Suspense fallback={<PageLoadingFallback />}>
                        <StockQRPage type="storage" />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/stock/qr/bin/:id"
                    element={
                      <Suspense fallback={<PageLoadingFallback />}>
                        <StockQRPage type="bin" />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/material-movements/qr/material/:id"
                    element={
                      <Suspense fallback={<PageLoadingFallback />}>
                        <MaterialMovementQrPage kind="material" />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/material-movements/qr/batch/:id"
                    element={
                      <Suspense fallback={<PageLoadingFallback />}>
                        <MaterialMovementQrPage kind="batch" />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/purchase-orders/qr/:id"
                    element={
                      <Suspense fallback={<PageLoadingFallback />}>
                        <PurchaseOrderQrPage />
                      </Suspense>
                    }
                  />
                  {/* Rutas protegidas con layout persistente */}
                  <Route
                    element={
                      <RequireAuth>
                        <AppLayout />
                      </RequireAuth>
                    }
                  >
                    <Route
                      path="/dashboard"
                      element={
                        <RequirePermission permission="MATERIALMOVEMENT.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <DashboardPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <Suspense fallback={<PageLoadingFallback />}>
                          <ProfilePage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/users/new"
                      element={
                        <RequirePermission permission="USER.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <UserFormPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/users/:id/edit"
                      element={
                        <RequirePermission permission="USER.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <UserFormPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/users"
                      element={
                        <RequirePermission permission="USER.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <UsersPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/organization-positions"
                      element={
                        <RequirePermission permission="ORG_POSITION.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <OrganizationPositionsPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/roles"
                      element={
                        <RequirePermission permission="ROLE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <RolesPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/roles/new"
                      element={
                        <RequirePermission permission="ROLE.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <RoleFormPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/roles/:id/edit"
                      element={
                        <RequirePermission permission="ROLE.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <RoleFormPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/permissions"
                      element={
                        <RequirePermission permission="PERMISSION.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PermissionsPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Master Data Routes */}
                    <Route
                      path="/materials"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.MATERIALS.READ}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MaterialListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/materials/new"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.MATERIALS.CREATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MaterialForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/materials/import"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.MATERIALS.CREATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MaterialImportPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/materials/:id"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.MATERIALS.READ}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MaterialDetailPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/materials/:id/edit"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.MATERIALS.UPDATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MaterialForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/material-unit-conversions"
                      element={
                        <RequirePermission
                          permission={
                            PERMISSIONS.MATERIAL_UNIT_CONVERSIONS.READ
                          }
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MaterialUnitConversionListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/material-unit-conversions/new"
                      element={
                        <RequirePermission
                          permission={
                            PERMISSIONS.MATERIAL_UNIT_CONVERSIONS.CREATE
                          }
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MaterialUnitConversionForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/material-unit-conversions/edit"
                      element={
                        <RequirePermission
                          permission={
                            PERMISSIONS.MATERIAL_UNIT_CONVERSIONS.UPDATE
                          }
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MaterialUnitConversionForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/plant-types"
                      element={
                        <RequirePermission permission="PLANTTYPE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PlantTypeListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/plant-types/new"
                      element={
                        <RequirePermission permission="PLANTTYPE.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PlantTypeForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/plant-types/:id/edit"
                      element={
                        <RequirePermission permission="PLANTTYPE.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PlantTypeForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/material-groups"
                      element={
                        <RequirePermission permission="MATERIALGROUP.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MaterialGroupListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/material-groups/new"
                      element={
                        <RequirePermission permission="MATERIALGROUP.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MaterialGroupForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/material-groups/import"
                      element={
                        <RequirePermission permission="MATERIALGROUP.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MaterialGroupImportPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/material-groups/:id/edit"
                      element={
                        <RequirePermission permission="MATERIALGROUP.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MaterialGroupForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/plants"
                      element={
                        <RequirePermission permission="PLANT.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PlantListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/plants/new"
                      element={
                        <RequirePermission permission="PLANT.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PlantForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/plants/:id/edit"
                      element={
                        <RequirePermission permission="PLANT.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PlantForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/storage-locations"
                      element={
                        <RequirePermission permission="STORAGELOC.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <StorageLocListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/storage-locations/new"
                      element={
                        <RequirePermission permission="STORAGELOC.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <StorageLocForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/storage-locations/:id/edit"
                      element={
                        <RequirePermission permission="STORAGELOC.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <StorageLocForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/storage-locations/:storageLocId/bins"
                      element={
                        <RequirePermission permission="STORAGEBIN.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <StorageBinListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/storage-loc-templates"
                      element={
                        <RequirePermission permission="STORAGELOCLAYOUTTEMPLATE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <StorageLocTemplateListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/storage-loc-templates/new"
                      element={
                        <RequirePermission permission="STORAGELOCLAYOUTTEMPLATE.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <StorageLocTemplateForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/storage-loc-templates/:id/edit"
                      element={
                        <RequirePermission permission="STORAGELOCLAYOUTTEMPLATE.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <StorageLocTemplateForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/batches"
                      element={
                        <RequirePermission permission="BATCH.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <BatchListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/batches/new"
                      element={
                        <RequirePermission permission="BATCH.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <BatchForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/batches/import"
                      element={
                        <RequirePermission permission="BATCH.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <BatchImportPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/batches/:id"
                      element={
                        <RequirePermission permission="BATCH.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <BatchForm mode="view" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/batches/:id/edit"
                      element={
                        <RequirePermission permission="BATCH.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <BatchForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/batch-templates"
                      element={
                        <RequirePermission permission="BATCHTEMPLATE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <BatchTemplateListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/batch-templates/new"
                      element={
                        <RequirePermission permission="BATCHTEMPLATE.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <BatchTemplateFormPage mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/batch-templates/:id"
                      element={
                        <RequirePermission permission="BATCHTEMPLATE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <BatchTemplateFormPage mode="view" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/batch-templates/:id/edit"
                      element={
                        <RequirePermission permission="BATCHTEMPLATE.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <BatchTemplateFormPage mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/units"
                      element={
                        <RequirePermission permission="UNIT.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <UnitsListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/units/new"
                      element={
                        <RequirePermission permission="UNIT.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <UnitsForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/units/import"
                      element={
                        <RequirePermission permission="UNIT.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <UnitImportPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/units/:id/edit"
                      element={
                        <RequirePermission permission="UNIT.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <UnitsForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* New Master Data Routes */}
                    <Route
                      path="/cost-centers"
                      element={
                        <RequirePermission permission="COSTCENTER.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CostCenterListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/cost-centers/new"
                      element={
                        <RequirePermission permission="COSTCENTER.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CostCenterForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/cost-centers/import"
                      element={
                        <RequirePermission permission="COSTCENTER.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CostCenterImportPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/cost-centers/:id/edit"
                      element={
                        <RequirePermission permission="COSTCENTER.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CostCenterForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/companies"
                      element={
                        <RequirePermission permission="COMPANY.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CompanyListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/companies/new"
                      element={
                        <RequirePermission permission="COMPANY.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CompanyForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/companies/:id/edit"
                      element={
                        <RequirePermission permission="COMPANY.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CompanyForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/production-orders"
                      element={
                        <RequirePermission permission="PRODUCTIONORDER.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <ProductionOrderListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/production-orders/new"
                      element={
                        <RequirePermission permission="PRODUCTIONORDER.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <ProductionOrderForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/production-orders/:id/edit"
                      element={
                        <RequirePermission permission="PRODUCTIONORDER.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <ProductionOrderForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/movement-types"
                      element={
                        <RequirePermission permission="MOVEMENTTYPE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MovementTypeListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/movement-types/new"
                      element={
                        <RequirePermission permission="MOVEMENTTYPE.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MovementTypeForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/movement-types/import"
                      element={
                        <RequirePermission permission="MOVEMENTTYPE.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MovementTypeImportPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/movement-types/:id/edit"
                      element={
                        <RequirePermission permission="MOVEMENTTYPE.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MovementTypeForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/vendors"
                      element={
                        <RequirePermission permission="VENDOR.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <VendorListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/vendors/new"
                      element={
                        <RequirePermission permission="VENDOR.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <VendorForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/vendors/:id/edit"
                      element={
                        <RequirePermission permission="VENDOR.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <VendorForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/vendor-services"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.VENDOR_SERVICE.READ}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <VendorServiceListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/vendor-services/new"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.VENDOR_SERVICE.CREATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <VendorServiceForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/vendor-services/:id/edit"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.VENDOR_SERVICE.UPDATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <VendorServiceForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/customers"
                      element={
                        <RequirePermission permission="CUSTOMER.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CustomerListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/customers/new"
                      element={
                        <RequirePermission permission="CUSTOMER.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CustomerForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/customers/:id/edit"
                      element={
                        <RequirePermission permission="CUSTOMER.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CustomerForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/purchase-orders"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.PURCHASE_ORDER.READ}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PurchaseOrderListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/purchase-orders/new"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.PURCHASE_ORDER.CREATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PurchaseOrderForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/purchase-orders/:id/edit"
                      element={
                        <RequirePermission
                          permission={[
                            PERMISSIONS.PURCHASE_ORDER.READ,
                            PERMISSIONS.PURCHASE_ORDER.UPDATE,
                          ]}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PurchaseOrderForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/sales-orders"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.SALES_ORDER.READ}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <SalesOrderListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/sales-orders/new"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.SALES_ORDER.CREATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <SalesOrderForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/sales-orders/:id/edit"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.SALES_ORDER.UPDATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <SalesOrderForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/payment-terms"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.PAYMENT_TERM.READ}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PaymentTermListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/payment-terms/new"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.PAYMENT_TERM.CREATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PaymentTermForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/payment-terms/:id/edit"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.PAYMENT_TERM.UPDATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PaymentTermForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/commercial-business-lines"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.COMMERCIAL_BUSINESS_LINE.READ}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CommercialBusinessLineListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/commercial-business-lines/new"
                      element={
                        <RequirePermission
                          permission={
                            PERMISSIONS.COMMERCIAL_BUSINESS_LINE.CREATE
                          }
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CommercialBusinessLineForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/commercial-business-lines/:id/edit"
                      element={
                        <RequirePermission
                          permission={
                            PERMISSIONS.COMMERCIAL_BUSINESS_LINE.UPDATE
                          }
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CommercialBusinessLineForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/document-classes"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.DOCUMENT_CLASS.READ}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CommercialDocumentClassListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/document-classes/new"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.DOCUMENT_CLASS.CREATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CommercialDocumentClassForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/document-classes/:id/edit"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.DOCUMENT_CLASS.UPDATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CommercialDocumentClassForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Material Movement Routes */}
                    <Route
                      path="/material-movements"
                      element={
                        <RequirePermission permission="MATERIALMOVEMENT.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MaterialMovementListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/material-movements/new"
                      element={
                        <RequirePermission permission="MATERIALMOVEMENT.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MovementFormPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/material-movements/:id"
                      element={
                        <RequirePermission permission="MATERIALMOVEMENT.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MovementFormPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/work-orders/new"
                      element={
                        <RequirePermission permission="WORKORDER.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <WorkOrderFormPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/work-orders"
                      element={
                        <RequirePermission permission="WORKORDER.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <WorkOrderListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/work-orders/:id"
                      element={
                        <RequirePermission permission="WORKORDER.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <WorkOrderDetailPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/work-order-templates"
                      element={
                        <RequirePermission permission="WORKORDERTEMPLATE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <WorkOrderTemplateListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/work-order-templates/new"
                      element={
                        <RequirePermission permission="WORKORDERTEMPLATE.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <WorkOrderTemplateFormPage mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/work-order-templates/:id"
                      element={
                        <RequirePermission permission="WORKORDERTEMPLATE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <WorkOrderTemplateDetailPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/work-order-templates/:id/edit"
                      element={
                        <RequirePermission permission="WORKORDERTEMPLATE.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <WorkOrderTemplateFormPage mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/work-order-activities"
                      element={
                        <RequirePermission permission="WORKORDERACTIVITY.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <WorkOrderActivityListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/work-order-activities/new"
                      element={
                        <RequirePermission permission="WORKORDERACTIVITY.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <WorkOrderActivityFormPage mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/work-order-activities/:id/edit"
                      element={
                        <RequirePermission permission="WORKORDERACTIVITY.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <WorkOrderActivityFormPage mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />

                    <Route
                      path="/industrial-formulas"
                      element={
                        <RequirePermission
                          permission={INDUSTRIAL_FORMULA_READ_PERMISSION}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <IndustrialFormulaListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/industrial-formulas/new"
                      element={
                        <RequirePermission
                          permission={INDUSTRIAL_FORMULA_CREATE_PERMISSION}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <IndustrialFormulaEditorPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/industrial-formulas/:id/edit"
                      element={
                        <RequirePermission
                          permission={INDUSTRIAL_FORMULA_UPDATE_PERMISSION}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <IndustrialFormulaEditorPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/industrial-formulas/:id/projections"
                      element={
                        <RequirePermission
                          permission={INDUSTRIAL_FORMULA_READ_PERMISSION}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <IndustrialFormulaProjectionsPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Dispatch Guide Routes */}
                    <Route
                      path="/dispatch-guides"
                      element={
                        <RequirePermission permission="MATERIALMOVEMENT.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <DispatchGuideListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/dispatch-guide-statuses"
                      element={
                        <RequirePermission permission="DISPATCHGUIDESTATUS.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <DispatchGuideStatusListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/dispatch-guide-statuses/new"
                      element={
                        <RequirePermission permission="DISPATCHGUIDESTATUS.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <DispatchGuideStatusForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/dispatch-guide-statuses/:id/edit"
                      element={
                        <RequirePermission permission="DISPATCHGUIDESTATUS.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <DispatchGuideStatusForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/dispatch-guide-status-transitions"
                      element={
                        <RequirePermission permission="DISPATCHGUIDESTATUS.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <DispatchGuideStatusTransitionListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/dispatch-guide-status-transitions/new"
                      element={
                        <RequirePermission permission="DISPATCHGUIDESTATUS.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <DispatchGuideStatusTransitionForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/dispatch-guide-status-transitions/:id/edit"
                      element={
                        <RequirePermission permission="DISPATCHGUIDESTATUS.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <DispatchGuideStatusTransitionForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/dispatch-flow-profiles"
                      element={
                        <RequirePermission permission="DISPATCHGUIDESTATUS.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <DispatchFlowProfileListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/dispatch-flow-profiles/new"
                      element={
                        <RequirePermission permission="DISPATCHGUIDESTATUS.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <DispatchFlowProfileForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/dispatch-flow-profiles/:id"
                      element={
                        <RequirePermission permission="DISPATCHGUIDESTATUS.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <DispatchFlowProfileForm mode="view" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/dispatch-flow-profiles/:id/edit"
                      element={
                        <RequirePermission permission="DISPATCHGUIDESTATUS.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <DispatchFlowProfileForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Approval Flow Routes */}
                    <Route
                      path="/approval-flows"
                      element={
                        <RequirePermission permission="APPROVAL_FLOW.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <ApprovalFlowListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/approval-flows/new"
                      element={
                        <RequirePermission permission="APPROVAL_FLOW.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <ApprovalFlowFormPage mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/approval-flows/:id/edit"
                      element={
                        <RequirePermission permission="APPROVAL_FLOW.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <ApprovalFlowFormPage mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Carrier Type Routes */}
                    <Route
                      path="/carrier-types"
                      element={
                        <RequirePermission permission="CARRIERTYPE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CarrierTypeListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/carrier-types/new"
                      element={
                        <RequirePermission permission="CARRIERTYPE.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CarrierTypeForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/carrier-types/:id/edit"
                      element={
                        <RequirePermission permission="CARRIERTYPE.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CarrierTypeForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Carrier Routes */}
                    <Route
                      path="/carriers"
                      element={
                        <RequirePermission permission="CARRIER.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CarrierListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/carriers/new"
                      element={
                        <RequirePermission permission="CARRIER.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CarrierForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/carriers/:id/edit"
                      element={
                        <RequirePermission permission="CARRIER.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CarrierForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Driver Routes */}
                    <Route
                      path="/drivers"
                      element={
                        <RequirePermission permission="DRIVER.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <DriverListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/drivers/new"
                      element={
                        <RequirePermission permission="DRIVER.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <DriverForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/drivers/:id/edit"
                      element={
                        <RequirePermission permission="DRIVER.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <DriverForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Vehicle Routes */}
                    <Route
                      path="/vehicles"
                      element={
                        <RequirePermission permission="VEHICLE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <VehicleListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/vehicles/new"
                      element={
                        <RequirePermission permission="VEHICLE.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <VehicleForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/vehicles/:id/edit"
                      element={
                        <RequirePermission permission="VEHICLE.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <VehicleForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Vehicle Assignment Routes */}
                    <Route
                      path="/vehicle-assignments"
                      element={
                        <RequirePermission permission="VEHICLEASSIGNMENT.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <VehicleAssignmentListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/vehicle-assignments/new"
                      element={
                        <RequirePermission permission="VEHICLEASSIGNMENT.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <VehicleAssignmentForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/vehicle-assignments/:id/edit"
                      element={
                        <RequirePermission permission="VEHICLEASSIGNMENT.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <VehicleAssignmentForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Field Routes */}
                    <Route
                      path="/fields"
                      element={
                        <RequirePermission permission="FIELD.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <FieldListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/fields/new"
                      element={
                        <RequirePermission permission="FIELD.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <FieldForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/fields/:id/edit"
                      element={
                        <RequirePermission permission="FIELD.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <FieldForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Employee Routes */}
                    <Route
                      path="/employees"
                      element={
                        <RequirePermission permission="EMPLOYEE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <EmployeeListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/employees/new"
                      element={
                        <RequirePermission permission="EMPLOYEE.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <EmployeeForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/employees/:id/edit"
                      element={
                        <RequirePermission permission="EMPLOYEE.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <EmployeeForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Cuartel Routes */}
                    <Route
                      path="/cuarteles"
                      element={
                        <RequirePermission permission="CUARTEL.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CuartelListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/cuarteles/new"
                      element={
                        <RequirePermission permission="CUARTEL.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CuartelForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/cuarteles/:id/edit"
                      element={
                        <RequirePermission permission="CUARTEL.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CuartelForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Attendance Routes */}
                    <Route
                      path="/attendance"
                      element={
                        <RequirePermission permission="ATTENDANCE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <AttendanceListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/attendance/daily"
                      element={
                        <RequirePermission permission="ATTENDANCE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <AttendanceDailyPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/labor-activities"
                      element={
                        <RequirePermission permission="LABOR_ACTIVITY.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <LaborActivityListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/labor-activities/new"
                      element={
                        <RequirePermission permission="LABOR_ACTIVITY.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <LaborActivityForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/labor-activities/:id/edit"
                      element={
                        <RequirePermission permission="LABOR_ACTIVITY.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <LaborActivityForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/labor-rates"
                      element={
                        <RequirePermission permission="LABOR_RATE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <LaborRateListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/labor-rates/new"
                      element={
                        <RequirePermission permission="LABOR_RATE.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <LaborRateForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/labor-rates/:id/edit"
                      element={
                        <RequirePermission permission="LABOR_RATE.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <LaborRateForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/labor-rate-generals"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.LABOR_RATE_GENERAL.READ}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <LaborRateGeneralListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/labor-rate-generals/new"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.LABOR_RATE_GENERAL.CREATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <LaborRateGeneralForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/labor-rate-generals/:id/edit"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.LABOR_RATE_GENERAL.UPDATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <LaborRateGeneralForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/especies"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.SPECIES.READ}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <SpeciesListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/especies/new"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.SPECIES.CREATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <SpeciesForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/especies/:id/edit"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.SPECIES.UPDATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <SpeciesForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/variedades"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.VARIETY.READ}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <VarietyListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/variedades/new"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.VARIETY.CREATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <VarietyForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/variedades/:id/edit"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.VARIETY.UPDATE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <VarietyForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/labor-retention"
                      element={
                        <RequirePermission permission="LABOR_RETENTION.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <LaborRetentionListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/labor-retention/new"
                      element={
                        <RequirePermission permission="LABOR_RETENTION.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <LaborRetentionForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/labor-retention/:id/edit"
                      element={
                        <RequirePermission permission="LABOR_RETENTION.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <LaborRetentionForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/labor-tickets"
                      element={
                        <RequirePermission permission="LABOR_TICKET.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <LaborTicketListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/labor-tickets/daily"
                      element={
                        <RequirePermission permission="LABOR_TICKET.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <LaborTicketDailyPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Route Routes */}
                    <Route
                      path="/routes"
                      element={
                        <RequirePermission permission="ROUTE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <RouteListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/routes/new"
                      element={
                        <RequirePermission permission="ROUTE.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <RouteForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/routes/:id/edit"
                      element={
                        <RequirePermission permission="ROUTE.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <RouteForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Trip Routes */}
                    <Route
                      path="/trips"
                      element={
                        <RequirePermission permission="TRIP.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <TripListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/trips/new"
                      element={
                        <RequirePermission permission="TRIP.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <TripForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/trips/:id/edit"
                      element={
                        <RequirePermission permission="TRIP.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <TripForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/trips/:id/execute"
                      element={
                        <RequirePermission
                          permission={PERMISSIONS.TRIP.EXECUTE}
                        >
                          <Suspense fallback={<PageLoadingFallback />}>
                            <TripExecutePage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/trips/:id/track"
                      element={
                        <RequirePermission permission={PERMISSIONS.TRIP.READ}>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <TripTrackPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Stock Routes */}
                    <Route
                      path="/stock"
                      element={
                        <RequirePermission permission="MATERIALMOVEMENT.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <StockListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Platform Routes */}
                    <Route
                      path="/subscriptions"
                      element={
                        <RequirePermission permission="SUBSCRIPTION.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <SubscriptionListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/subscriptions/new"
                      element={
                        <RequirePermission permission="SUBSCRIPTION.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <SubscriptionForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/subscriptions/:id/edit"
                      element={
                        <RequirePermission permission="SUBSCRIPTION.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <SubscriptionForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/subscriptions/:id/users"
                      element={
                        <RequirePermission permission="USER.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <SubscriptionUsersPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/plans"
                      element={
                        <RequirePermission permission="PLAN.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PlanListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/plans/new"
                      element={
                        <RequirePermission permission="PLAN.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PlanFormPage mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/plans/:id/edit"
                      element={
                        <RequirePermission permission="PLAN.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PlanFormPage mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/plan-prices"
                      element={
                        <RequirePermission permission="PLAN_PRICE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PlanPriceListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/plan-prices/new"
                      element={
                        <RequirePermission permission="PLAN_PRICE.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PlanPriceFormPage mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/plan-prices/:id/edit"
                      element={
                        <RequirePermission permission="PLAN_PRICE.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PlanPriceFormPage mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/invoices"
                      element={
                        <RequirePermission permission="INVOICE.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <InvoiceListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/audit"
                      element={
                        <RequirePermission permission="AUDIT.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <AuditListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/currencies"
                      element={
                        <RequirePermission permission="CURRENCY.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CurrencyListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/currencies/new"
                      element={
                        <RequirePermission permission="CURRENCY.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CurrencyForm mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/currencies/:id/edit"
                      element={
                        <RequirePermission permission="CURRENCY.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CurrencyForm mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/units-core"
                      element={
                        <RequirePermission permission="UNIT.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <UnitCoreListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/units-core/new"
                      element={
                        <RequirePermission permission="UNIT.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <UnitCoreFormPage mode="create" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/units-core/:id/edit"
                      element={
                        <RequirePermission permission="UNIT.UPDATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <UnitCoreFormPage mode="edit" />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    {/* Material Request Routes */}
                    <Route
                      path="/material-requests"
                      element={
                        <RequirePermission permission="MATERIALREQUEST.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MaterialRequestListPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/material-requests/new"
                      element={
                        <RequirePermission permission="MATERIALREQUEST.CREATE">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MaterialRequestCreatePage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/material-requests/:id"
                      element={
                        <RequirePermission permission="MATERIALREQUEST.READ">
                          <Suspense fallback={<PageLoadingFallback />}>
                            <MaterialRequestDetailPage />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/"
                      element={
                        <Suspense fallback={<PageLoadingFallback />}>
                          <WelcomePage />
                        </Suspense>
                      }
                    />
                  </Route>
                  <Route
                    path="*"
                    element={
                      <Suspense fallback={<PageLoadingFallback />}>
                        <NotFoundPage />
                      </Suspense>
                    }
                  />
                </Routes>
                <PermissionHintFab />
              </div>
            </Router>
          </AuthErrorBoundary>
        </TokenRefreshProvider>
      </PermissionHintProvider>
    </AuthProvider>
  );
}

export default App;
